import { bytesToBase64, base64ToBytes } from '../lib/utils';

/**
 * Typed wrapper around Office.context.document.settings — the Office-provided
 * per-document storage that survives Word close/reopen (and syncs via OneDrive
 * if the file is cloud-hosted).
 *
 * We store:
 *   - chatId            — stable conversation id for this document's edits
 *   - currentS3Key      — the most recent edited DOCX in S3
 *   - lastUploadHash    — for upload-once optimization
 *   - snapshots         — last N pre-edit DOCX bytes (base64) for user-triggered revert
 *
 * Settings storage is capped by Office (~2 MB serialized per document on some
 * clients). We cap `snapshots` at 3 to stay well under budget.
 */

const KEY = 'nyayassist:wordEditState';
const SNAPSHOT_CAP = 3;

export interface WordEditState {
  chatId: string | null;
  currentS3Key: string | null;
  lastUploadHash: string | null;
  /** Oldest first; pushSnapshot() trims from the head when full */
  snapshots: Array<{ takenAt: number; base64: string }>;
  /**
   * When set, this Word document is linked to an existing `chat_type:'draft'`
   * chat (either web- or plugin-originated). Edit-mode messages are routed
   * into this chat instead of a new `word_edit` thread so follow-up edits
   * show up in the web's draft sidebar.
   */
  linkedDraftChatId?: string | null;
  linkedDraftName?: string | null;
  /**
   * Version string + SHA-256 of the last DOCX bytes applied to this document.
   * Compared against the chat's `metadata.latest_version` /
   * `metadata.docx_sha256` on boot to detect whether a newer version has been
   * produced elsewhere (e.g. via the web UI, or by the same user in another
   * Word window) — surfaced to the user as a "Newer version available" banner.
   */
  lastAppliedVersion?: string | null;
  lastAppliedSha256?: string | null;
  /** Banner dismissals — keyed by `version:sha256` so a newer one re-prompts. */
  dismissedUpdateKeys?: string[];
  /**
   * chat_ids for which the user said "Not now" to the ask-to-link banner.
   * Prevents nagging on the same file + same detected chat.
   */
  declinedAutoLinkChatIds?: string[];
}

const EMPTY: WordEditState = {
  chatId: null,
  currentS3Key: null,
  lastUploadHash: null,
  snapshots: [],
  linkedDraftChatId: null,
  linkedDraftName: null,
  lastAppliedVersion: null,
  lastAppliedSha256: null,
  dismissedUpdateKeys: [],
  declinedAutoLinkChatIds: [],
};

export function getWordEditState(): WordEditState {
  const raw = Office.context.document.settings.get(KEY) as unknown;
  if (!raw) return { ...EMPTY };
  if (typeof raw === 'object' && raw) {
    const r = raw as Partial<WordEditState>;
    return {
      chatId: r.chatId ?? null,
      currentS3Key: r.currentS3Key ?? null,
      lastUploadHash: r.lastUploadHash ?? null,
      snapshots: Array.isArray(r.snapshots) ? r.snapshots : [],
      linkedDraftChatId: r.linkedDraftChatId ?? null,
      linkedDraftName: r.linkedDraftName ?? null,
      lastAppliedVersion: r.lastAppliedVersion ?? null,
      lastAppliedSha256: r.lastAppliedSha256 ?? null,
      dismissedUpdateKeys: Array.isArray(r.dismissedUpdateKeys)
        ? r.dismissedUpdateKeys
        : [],
      declinedAutoLinkChatIds: Array.isArray(r.declinedAutoLinkChatIds)
        ? r.declinedAutoLinkChatIds
        : [],
    };
  }
  return { ...EMPTY };
}

export async function setWordEditState(patch: Partial<WordEditState>): Promise<void> {
  const current = getWordEditState();
  const next = { ...current, ...patch };
  Office.context.document.settings.set(KEY, next);
  await persist();
}

/** Push a pre-edit DOCX snapshot for revert. Trims to SNAPSHOT_CAP. */
export async function pushSnapshot(bytes: Uint8Array): Promise<void> {
  const current = getWordEditState();
  const base64 = bytesToBase64(bytes);
  const snapshots = [...current.snapshots, { takenAt: Date.now(), base64 }];
  while (snapshots.length > SNAPSHOT_CAP) snapshots.shift();
  await setWordEditState({ snapshots });
}

/** Pop the most recent snapshot (for revert) and return its bytes. */
export async function popSnapshot(): Promise<Uint8Array | null> {
  const current = getWordEditState();
  if (current.snapshots.length === 0) return null;
  const snaps = [...current.snapshots];
  const last = snaps.pop()!;
  await setWordEditState({ snapshots: snaps });
  return base64ToBytes(last.base64);
}

/** Clear all add-in state for this document (used on logout or manual reset). */
export async function clearWordEditState(): Promise<void> {
  Office.context.document.settings.set(KEY, null);
  await persist();
}

function persist(): Promise<void> {
  return new Promise((resolve, reject) => {
    Office.context.document.settings.saveAsync((result) => {
      if (result.status !== Office.AsyncResultStatus.Succeeded) {
        reject(new Error(result.error?.message ?? 'settings.saveAsync failed'));
        return;
      }
      resolve();
    });
  });
}
